// resources/js/lib/voip/api.ts

import axios from 'axios';
import type { SipAccount, SoftphoneTokenResponse } from '@/types/voip';

const API_PREFIX = '/api/voip';

/* ─────────────────────────────────────────────────────────────────
 * SIP Accounts CRUD
 * ─────────────────────────────────────────────────────────────── */

export async function listSipAccounts(): Promise<{ data: SipAccount[] }> {
  const response = await axios.get(`${API_PREFIX}/sip-accounts`);
  return response.data;
}

export async function getSipAccount(id: number): Promise<{ data: SipAccount }> {
  const response = await axios.get(`${API_PREFIX}/sip-accounts/${id}`);
  return response.data;
}

export async function createSipAccount(data: Partial<SipAccount> & { sip_password?: string }): Promise<{ data: SipAccount }> {
  const response = await axios.post(`${API_PREFIX}/sip-accounts`, data);
  return response.data;
}

export async function updateSipAccount(id: number, data: Partial<SipAccount> & { sip_password?: string }): Promise<{ data: SipAccount }> {
  const response = await axios.patch(`${API_PREFIX}/sip-accounts/${id}`, data);
  return response.data;
}

export async function deleteSipAccount(id: number): Promise<void> {
  await axios.delete(`${API_PREFIX}/sip-accounts/${id}`);
}

export async function setDefaultSipAccount(id: number): Promise<{ data: SipAccount }> {
  const response = await axios.post(`${API_PREFIX}/sip-accounts/${id}/default`);
  return response.data;
}

export async function toggleSipAccountActive(id: number, is_active: boolean): Promise<{ data: SipAccount }> {
  const response = await axios.post(`${API_PREFIX}/sip-accounts/${id}/toggle`, { is_active });
  return response.data;
}

export async function testSipAccount(id: number): Promise<{
  data: {
    registered: boolean;
    sip_code: number | null;
    sip_reason: string | null;
    latency_ms: number | null;
    error: string | null;
    account: SipAccount;
  };
}> {
  const response = await axios.post(`${API_PREFIX}/sip-accounts/${id}/test`);
  return response.data;
}

export async function getSipAccountLogs(id: number, perPage: number = 50): Promise<{ data: any[] }> {
  const response = await axios.get(`${API_PREFIX}/sip-accounts/${id}/logs`, {
    params: { per_page: perPage },
  });
  return response.data;
}

/* ─────────────────────────────────────────────────────────────────
 * SIP Providers (catálogo)
 * ─────────────────────────────────────────────────────────────── */

export async function listSipProviders(): Promise<{ data: any[] }> {
  const response = await axios.get(`${API_PREFIX}/sip-providers`);
  return response.data;
}

/* ─────────────────────────────────────────────────────────────────
 * Softphone tokens
 * ─────────────────────────────────────────────────────────────── */

export async function getSoftphoneToken(sipAccountId: number): Promise<SoftphoneTokenResponse> {
  const response = await axios.post(`${API_PREFIX}/sip-accounts/${sipAccountId}/softphone-token`);
  return response.data;
}

export async function refreshSoftphoneToken(sipAccountId: number, nonce: string): Promise<SoftphoneTokenResponse> {
  const response = await axios.post(`${API_PREFIX}/sip-accounts/${sipAccountId}/softphone-token/${nonce}/refresh`);
  return response.data;
}

/* ─────────────────────────────────────────────────────────────────
 * Browser registration events (REGISTER/UNREGISTER outcome)
 * ─────────────────────────────────────────────────────────────── */

export async function reportRegistrationEvent(
  sipAccountId: number,
  data: { check_type: string; status: string; sip_code?: number; sip_reason?: string; error?: string }
): Promise<void> {
  await axios.post(`${API_PREFIX}/sip-accounts/${sipAccountId}/registration-events`, data);
}

/* ─────────────────────────────────────────────────────────────────
 * Call sessions
 * ─────────────────────────────────────────────────────────────── */

export async function startCallSession(data: {
  sip_account_id: number;
  to: string;
  from?: string;
  call_id?: number;
}): Promise<{ data: { id: number } }> {
  const response = await axios.post(`${API_PREFIX}/call-sessions`, data);
  return response.data;
}

export async function endCallSession(callSessionId: number, data?: { cause?: string; duration_seconds?: number }): Promise<void> {
  await axios.post(`${API_PREFIX}/call-sessions/${callSessionId}/end`, data || {});
}

export async function pushCallStats(callSessionId: number, stats: any): Promise<void> {
  await axios.post(`${API_PREFIX}/call-sessions/${callSessionId}/stats`, stats);
}

/* ─────────────────────────────────────────────────────────────────
 * Live calls + recordings (Module 7)
 * ─────────────────────────────────────────────────────────────── */

export async function listLiveCalls(): Promise<{ data: any[] }> {
  const response = await axios.get(`${API_PREFIX}/live-calls`);
  return response.data;
}

export async function getCallRecording(callId: number): Promise<{ data: { url: string; expires_at: string | null } }> {
  const response = await axios.get(`${API_PREFIX}/calls/${callId}/recording`);
  return response.data;
}

export async function transferCall(callId: number, destination: string, attended: boolean = false): Promise<void> {
  await axios.post(`${API_PREFIX}/calls/${callId}/transfer`, { destination, attended });
}

export async function hangupCall(callId: number): Promise<void> {
  await axios.post(`${API_PREFIX}/calls/${callId}/hangup`);
}
